import { AlertTriangle, Lightbulb, Palette, Type } from 'lucide-react';
import { sichtbareFelder, type Vorlage } from '../../lib/mailBuilder/vorlagen';
import { MailVorschau } from '../../components/MailVorschau/MailVorschau';
import { FeldEingabe } from './FeldEingabe';
import { kontext, stichtagHinweis, type Stand } from './modell';

type Props = {
  v: Vorlage;
  stand: Stand;
  html: string;
  setFeld: (id: string, wert: string) => void;
  aendern: (teil: Partial<Stand>) => void;
  zeigeFehler: boolean;
};

export function SchrittInhalt({ v, stand, html, setFeld, aendern, zeigeFehler }: Props) {
  const alle = sichtbareFelder(v, stand.daten, stand.antworten);
  const felder = alle.filter((f) => f.gruppe !== 'gestaltung');
  const gestaltung = alle.filter((f) => f.gruppe === 'gestaltung');
  const k = kontext(stand);
  const vorschlaege = [...new Set(v.betreff(stand.daten, k).map((b) => b.replace(/\s+/g, ' ').trim()).filter((b) => b.length > 3))];
  const vorschau = v.vorschautext(stand.daten, k).trim();
  const hinweis = stichtagHinweis(stand, v);

  return (
    <div className="as-zwei">
      <div className="as-felder" data-tour="assistent-arbeit">
        {felder.map((f) => (
          <FeldEingabe
            key={f.id}
            feld={f}
            daten={stand.daten}
            antworten={stand.antworten}
            onChange={(w) => setFeld(f.id, w)}
            setzen={setFeld}
            zeigeFehler={zeigeFehler}
          />
        ))}

        {gestaltung.length > 0 && (
          <section className="as-abschnitt as-gestaltung" data-tour="assistent-gestaltung">
            <h3 className="as-abschnitt-titel"><Palette size={16} aria-hidden="true" /> Gestaltung</h3>
            <p className="as-warum">Bannerbild, Symbol im Kopf und wie viele Emojis die Mail trägt. Alles andere — Farben, Schrift, Fußzeile — setzt das Mailtool im Randori-Design.</p>
            {gestaltung.map((f) => (
              <FeldEingabe
                key={f.id}
                feld={f}
                daten={stand.daten}
                antworten={stand.antworten}
                onChange={(w) => setFeld(f.id, w)}
                setzen={setFeld}
                zeigeFehler={zeigeFehler}
              />
            ))}
          </section>
        )}

        <section className="as-abschnitt as-betreff">
          <h3 className="as-abschnitt-titel"><Type size={16} aria-hidden="true" /> Betreff und Vorschautext</h3>
          <div className="form-group">
            <label className="form-label" htmlFor="betreff">Betreff<span className="as-pflicht" aria-hidden="true"> *</span></label>
            <input id="betreff" className={`form-input${zeigeFehler && !stand.betreff.trim() ? ' input-error' : ''}`} value={stand.betreff}
              maxLength={140} onChange={(e) => aendern({ betreff: e.target.value, betreffManuell: true })} aria-required="true" />
            <p className="form-hint">Auf dem Handy sind etwa 40 bis 50 Zeichen zu sehen — das Wichtigste nach vorn. ({stand.betreff.length} Zeichen)</p>
            {vorschlaege.length > 0 && (
              <div className="as-vorschlaege">
                <span className="as-vorschlaege-label"><Lightbulb size={13} aria-hidden="true" /> Vorschläge:</span>
                {vorschlaege.map((b) => (
                  <button key={b} type="button" className={`chip${stand.betreff === b ? ' active' : ''}`} onClick={() => aendern({ betreff: b, betreffManuell: true })}>
                    {b}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="vorschautext">Vorschautext (freiwillig)</label>
            <input id="vorschautext" className="form-input" value={stand.vorschautext} maxLength={120}
              onChange={(e) => aendern({ vorschautext: e.target.value, vorschauManuell: true })} />
            <p className="form-hint">
              Die graue Zeile im Posteingang neben dem Betreff (40 bis 90 Zeichen).
              {vorschau && vorschau !== stand.vorschautext && (
                <> Vorschlag: „{vorschau}" — <button type="button" className="zf-aendern" onClick={() => aendern({ vorschautext: vorschau, vorschauManuell: true })}>übernehmen</button></>
              )}
            </p>
          </div>
        </section>

        {hinweis && (
          <div className="alert alert-warning"><AlertTriangle size={16} aria-hidden="true" /><div>{hinweis}</div></div>
        )}
      </div>

      <div className="as-vorschau-spalte">
        <MailVorschau html={html} titel="So kommt die Mail an" tour="assistent-vorschau" />
      </div>
    </div>
  );
}
